import { Reducer } from 'redux';

import { KnownAction, PortalAssetsActions } from './Actions';

import {
    PortalText,
    PortalTextSectionIds,
} from '../../Models/Api/strongbox.financialportal';

import { ReadOverriddenAppSettingsValues } from '../AppSettings';

export type PortalTextSectionRoot = {
    id: PortalTextSectionIds;
}

export interface IPortalAssetsState {
    // Maps section ids to the values retrieved. In c# land, everything stored in
    // this map will be descended from a 'TextSection' which at the root level
    // contains nothing more than an id.  So stuff stored here will all be guaranteed
    // to have an id. Type checking on the back end form source JSON files will ensure
    // that the actual type stored for a secion id will be the correct type.
    textSections: Map<PortalTextSectionIds, PortalTextSectionRoot>;
    languageCode: string;   // something like "en"
    localeCode?: string;     // something like "us"
    textSectionsInFlight: PortalTextSectionIds[];   // Which section ids are currently being retrieved
}

const defaultState: IPortalAssetsState = {
    textSections: new Map<PortalTextSectionIds, PortalTextSectionRoot>(),
    languageCode: 'en',
    textSectionsInFlight: [],
}

const localStorageLanguageKey = 'strongboxPortalLanguageCode';

function MergePortalText(state: IPortalAssetsState, sectionIds: PortalTextSectionIds[], portalText: PortalText): IPortalAssetsState {
    if (!!state.textSections) {
        state.textSections = new Map<PortalTextSectionIds, PortalTextSectionRoot>(state.textSections);
    } else {
        state.textSections = new Map<PortalTextSectionIds, PortalTextSectionRoot>();
    }

    // Look at each section id that was part of the request.  Then look in the retrieved portal text and 
    // try to find that section id.   If the section was found in the result, set the value
    // state.textSections to the new value.
    sectionIds.forEach((sectionId) => {
        const indexSectionId = Object.keys(portalText).findIndex((portalTextSectionId) => {
            return sectionId === portalTextSectionId;
        });
        if (indexSectionId !== -1) {
            state.textSections.set(sectionId, Object.values(portalText)[indexSectionId]);
        }
    });

    return state;
}

export const reducer: Reducer<IPortalAssetsState, KnownAction> = (state: IPortalAssetsState | undefined, action: KnownAction): IPortalAssetsState => {
    let newState: IPortalAssetsState | undefined = undefined;

    switch (action.type) {
        case PortalAssetsActions.PortalAssetsInitialize: {
            newState = {
                ...(state || defaultState),
            }
            let lang = window.localStorage.getItem(localStorageLanguageKey);
            let supportMultipleLanguages = action.supportMultipleLanguages;

            const overrides = ReadOverriddenAppSettingsValues(true);
            if (!!overrides) {
                supportMultipleLanguages = overrides.allowLanguageSelection;
            }

            if (supportMultipleLanguages) {
                if (lang === null) {
                    lang = navigator.language;
                }
            }
            if (!!lang) {
                const langParts = lang.split('-');
                newState.languageCode = langParts[0].toLowerCase();
                if (langParts.length > 1) {
                    newState.localeCode = langParts[1].toLowerCase();
                }
            }
            else {
                newState.languageCode = 'en';
                newState.localeCode = undefined;
            }
            break;
        }
        case PortalAssetsActions.PortalAssetsRetrieveLoginText: {
            // nothing to do here yet.
            break;
        }
        case PortalAssetsActions.PortalAssetsRetrieveLoginTextCompleted: {
            if (!!action.portalLoginText) {
                newState = {
                    ...(state || defaultState),
                }
                if (!!newState.textSections) {
                    newState.textSections = new Map<PortalTextSectionIds, PortalTextSectionRoot>(newState.textSections);
                } else {
                    newState.textSections = new Map<PortalTextSectionIds, PortalTextSectionRoot>();
                }
                newState.textSections.set('loginPage', action.portalLoginText);
            }
            break;
        }
        case PortalAssetsActions.PortalAssetsRetrieveTimeoutText: {
            // nothing to do here yet.
            break;
        }
        case PortalAssetsActions.PortalAssetsRetrieveTimeoutTextCompleted: {
            if (!!action.portalTimeoutText) {
                newState = {
                    ...(state || defaultState),
                }
                if (!!newState.textSections) {
                    newState.textSections = new Map<PortalTextSectionIds, PortalTextSectionRoot>(newState.textSections);
                } else {
                    newState.textSections = new Map<PortalTextSectionIds, PortalTextSectionRoot>();
                }
                newState.textSections.set('timeoutMessaging', action.portalTimeoutText);
            }
            break;
        }
        case PortalAssetsActions.PortalAssetsRetrieveBorrowerAnonymousText: {
            // nothing to do here yet.
            break;
        }
        case PortalAssetsActions.PortalAssetsRetrieveBorrowerAnonymousTextCompleted: {
            if (!!action.portalBorrowerAnonymousText) {
                newState = {
                    ...(state || defaultState),
                }
                if (!!newState.textSections) {
                    newState.textSections = new Map<PortalTextSectionIds, PortalTextSectionRoot>(newState.textSections);
                } else {
                    newState.textSections = new Map<PortalTextSectionIds, PortalTextSectionRoot>();
                }
                newState.textSections.set('borrowerAnonymousSubmission', action.portalBorrowerAnonymousText);
            }
            break;
        }
        case PortalAssetsActions.PortalAssetsRetrieveAccountingSystemsText: {
            // nothing to do here yet.
            break;
        }
        case PortalAssetsActions.PortalAssetsRetrieveAccountingSystemsTextCompleted: {
            if (!!action.portalAccountingSystemsText) {
                newState = {
                    ...(state || defaultState),
                }
                if (!!newState.textSections) {
                    newState.textSections = new Map<PortalTextSectionIds, PortalTextSectionRoot>(newState.textSections);
                } else {
                    newState.textSections = new Map<PortalTextSectionIds, PortalTextSectionRoot>();
                }
                newState.textSections.set('accountingSystems', action.portalAccountingSystemsText.accountingSystemsText);
                newState.textSections.set('borrowerParameters', action.portalAccountingSystemsText.borrowerParametersText);
                // The connect modal title ("Connect to <system>") reads commonText.connectToAccountingPkg. That
                // common section isn't otherwise loaded in the anonymous flow (no user context yet), so it comes
                // back with this anonymous call — store it so the title resolves instead of "undefined".
                if (!!action.portalAccountingSystemsText.commonText) {
                    newState.textSections.set('commonText', action.portalAccountingSystemsText.commonText);
                }
            }
            break;
        }
        case PortalAssetsActions.PortalAssetsRetrieveText: {
            // Save the sectionIds so we can check on new requests and not add another request to go retrieve them
            newState = {
                ...(state || defaultState),
            }
            newState.textSectionsInFlight = newState.textSectionsInFlight.slice();
            action.sectionIds.forEach(sectionId => {
                const indexSection = newState!.textSectionsInFlight.findIndex(sectionIdInFlight => sectionIdInFlight === sectionId);
                if (indexSection === -1) {
                    newState!.textSectionsInFlight.push(sectionId);
                }
            });

            break;
        }
        case PortalAssetsActions.PortalAssetsRetrieveTextCompleted: {
            newState = {
                ...(state || defaultState),
            }
            if (!!action.portalTextResponse) {
                newState.textSectionsInFlight = newState.textSectionsInFlight.slice();
                newState = MergePortalText(newState, action.sectionIds, action.portalTextResponse.content);

            }
            // Remove the section ids for the action from the list of section ids in flight
            action.sectionIds.forEach(sectionId => {
                const indexSection = newState!.textSectionsInFlight.findIndex(sectionIdInFlight => sectionIdInFlight === sectionId);
                if (indexSection !== -1) {
                    newState!.textSectionsInFlight = newState!.textSectionsInFlight.slice(0, indexSection).concat(newState!.textSectionsInFlight.slice(indexSection + 1));
                }
            });

            break;
        }
        case PortalAssetsActions.PortalAssetsSetLanguageCode: {
            newState = {
                ...(state || defaultState),
            }
            const components = action.languageCode.split('-');
            if (components.length > 1) {
                newState.languageCode = components[0];
                newState.localeCode = components[1];
            }
            else {
                newState.languageCode = components[0];
                newState.localeCode = '';
            }
            newState.textSections = new Map<PortalTextSectionIds, PortalTextSectionRoot>();
            window.localStorage.setItem(localStorageLanguageKey, newState.languageCode);
            break;
        }
    }
    return newState ? newState : state ? state : defaultState;
}
